<script>
  import { onMount, onDestroy, createEventDispatcher } from 'svelte';
  import { goto } from '$app/navigation';
  import LinkedAccountsAjax from '@ajax/linked_accounts';
  import Auth from '@utils/auth';
  import Notification from '@utils/notification';
  import Track from '@utils/track';
  import Modal from '@components/Modal.svelte';
  import Avatar from '@components/Avatar.svelte';
  import Button from '@components/Button.svelte';
  import CreateAccountModal from '@app-components/modal/CreateAccountModal.svelte';

  export let showModal = false;
  export let mobile = false;
  export let currentUser;
  export let slideIn;

  let linkedAccountUsers = [];

  let step = 1;

  const setStep = (newStep) => {
    step = newStep;
  };

  const close = () => {
    dispatch('close');
    showModal = false;
  };

  let showCreateAccountModal = false;
  let signInView = false;
  $: linkMode = signInView ? 'login' : 'create';

  const createAndLinkNewAccount = () => {
    signInView = false;
    showCreateAccountModal = true;
  };

  const loginAndLinkExistingAccount = () => {
    signInView = true;
    showCreateAccountModal = true;
  };

  const oldUser = {
    email: currentUser.email,
    api_token: currentUser.api_token,
  };

  const switchAccountsAfterCreateCallback = async (newUser) => {
    try {
      await LinkedAccountsAjax.linkAccounts(oldUser.api_token, newUser.api_token, linkMode);

      Notification.send({
        text: `Success! You have linked the accounts for ${oldUser.email} and ${newUser.email}`,
        closeWith: ['button'],
      });
    } catch {
      Notification.send({
        text: `There was an error linking accounts. Please try again, or contact our support team.`,
        closeWith: ['button'],
      });
    } finally {
      close();
      goto(newUser.profile_path);
    }
  };

  const goBackFromStepOne = () => {
    dispatch('step-one-back-clicked');
    close();
  };

  const dispatch = createEventDispatcher();

  const loadLinkedAccounts = async () => {
    const linkedAcccountsResponse = await LinkedAccountsAjax.getCurrentUserLinkedAccounts();
    linkedAccountUsers = linkedAcccountsResponse.linked_accounts;
  };

  const switchToAccount = async (user) => {
    const resp = await LinkedAccountsAjax.switchToAccount(user.id);
    await Auth.load(currentUser, resp.token);

    goto(user.profile_path);
  };

  const trackSwitchAccountsMenuItemClicked = () => {
    Track.switchAccountsMenuItemClicked({
      currentUser: currentUser,
    });
  };

  const validateNotCurrentUser = async (emailValue) => {
    if (currentUser.email === emailValue) {
      return 'You are currently logged into this account!';
    }
    return undefined;
  };

  onMount(() => {
    loadLinkedAccounts();
    trackSwitchAccountsMenuItemClicked();
  });

  onDestroy(() => {});
</script>

<style style="text/scss" lang="scss">
  @import '../../vendor/design-system.scss';

  .ui-modal-body {
    padding: 20px;

    .ui-existing-accounts {
      @include typeface-b1;
      @include color('neutral-gray');
    }

    .ui-user-row {
      $ui-user-row-padding-x: 15px;
      @include typeface-b1;

      padding: 12px $ui-user-row-padding-x;
      border-radius: 15px;
      margin-left: $ui-user-row-padding-x * -1;
      width: calc(100% + 2 * $ui-user-row-padding-x);
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;

      &.first-row {
        margin-top: 24px;
      }

      .ui-user-item {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;

        .ui-user-name {
          margin-left: 16px;
        }
      }

      .ui-user-current-check {
        display: flex;
      }
    }

    .clickable {
      cursor: pointer;

      &:active,
      &:focus,
      &:hover {
        @include bg-color('purple-20');
      }
    }
  }

  .ui-link-buttons {
    margin-top: 60px;
    margin-bottom: 40px;
    padding: 0px 90px;
  }

  .mobile {
    .ui-link-buttons {
      margin-top: 30px;
      margin-bottom: 30px;
      padding: 0px 30px;
    }

    .ui-modal-body {
      padding: 0px 10px;
    }
  }
</style>

<div class="svelte-modal">
  {#if !showCreateAccountModal}
    <Modal
      style="border-radius: 15px; width: 550px;"
      mobile={mobile}
      on:close={close}
      show={showModal}
      showCloseButton={true}
      fadeInDuration={mobile ? 0 : 200}
      closeButtonStyle="position: absolute; top: 18px; right: 20px; padding: 0px;"
      closeButtonSize={mobile ? 'large' : 'normal'}
      modalLocation={mobile ? 'bottom' : 'top'}
      slideIn={slideIn}
      class="settings-modal"
      newCloseButtonStyle={true}>
      <div slot="header" class="header" class:mobile={mobile}>
        {#if step === 1}
          <div class="ui-modal-header">
            {#if mobile}
              <div class="ui-modal-header-back" on:click={() => goBackFromStepOne()}>
                <img src="/images/switch-accounts-modal/back-black.svg" alt="back-button" />
              </div>
            {/if}
            Switch accounts
          </div>
        {:else if step === 2}
          <div class="ui-modal-header">
            <div class="ui-modal-header-back" on:click={() => setStep(1)}>
              {#if mobile}
                <img src="/images/switch-accounts-modal/back-black.svg" alt="back-button" />
              {:else}
                <img src="/images/payout/back.svg" alt="back-button" />
              {/if}
            </div>
            Add another account
          </div>
        {/if}
      </div>

      <div slot="content" class="content" class:mobile={mobile}>
        {#if step == 1}
          <div class="ui-modal-body">
            {#if !mobile}
              <div class="ui-existing-accounts">Existing accounts</div>
            {/if}

            <!-- current user -->
            <div class="ui-user-row first-row">
              <div class="ui-user-item">
                <Avatar width={32} src={currentUser.avatar_url} user={currentUser} mobile={mobile} />
                <div class="ui-user-name">
                  {currentUser.name}
                </div>
              </div>
              <div class="ui-user-current-check">
                <img
                  src="/images/switch-accounts-modal/checkmark-circle.png"
                  srcset=" /images/switch-accounts-modal/checkmark-circle.png 1x, /images/switch-accounts-modal/checkmark-circle@2x.png 2x"
                  alt="checkmark-circle" />
              </div>
            </div>

            <!-- other (linked) accounts -->
            {#each linkedAccountUsers as user}
              <div class="ui-user-row clickable" on:click={() => switchToAccount(user)}>
                <div class="ui-user-item">
                  <Avatar width={32} src={user.avatar_url} user={user} mobile={mobile} />
                  <div class="ui-user-name">
                    {user.name}
                  </div>
                </div>
                <div class="ui-user-check-placeholder" />
              </div>
            {/each}

            <!-- link new account -->
            <div class="ui-user-row clickable" on:click={() => setStep(2)}>
              <div class="ui-user-item">
                <Avatar width={32} src="/images/switch-accounts-modal/add-account-button.png" />
                <div class="ui-user-name">Add Account</div>
              </div>
              <div class="ui-user-check-placeholder" />
            </div>
          </div>
        {:else if step == 2}
          <div class="ui-link-buttons">
            <div class="new-account-button">
              <Button colorScheme="purple" shadow={false} on:click={createAndLinkNewAccount}
                >Create a new account</Button>
            </div>

            <div class="existing-account-button">
              <Button colorScheme="white" on:click={loginAndLinkExistingAccount} disableHoverEffect={mobile}>
                Login to existing account
              </Button>
            </div>
          </div>
        {/if}
      </div>
    </Modal>
  {/if}
</div>
{#if showCreateAccountModal}
  <div>
    <CreateAccountModal
      mobile={mobile}
      successCallback={switchAccountsAfterCreateCallback}
      createOrLoginEmailValidationFn={validateNotCurrentUser}
      bind:showModal={showCreateAccountModal}
      signInView={signInView}
      inviteFlow={false}
      beforeDataSend={(data) => {
        data['landing_page'] = 'default';
        data['sign_up_source'] = {
          source: 'switch-accounts',
        };
        return data;
      }} />
  </div>
{/if}
